import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, errorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { ImportPanel } from "@/components/bilag/ImportPanel";
import { DocTypeBadge, AttachmentStatusBadge } from "@/components/bilag/AttachmentBadges";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Collapsible, CollapsibleContent } from "@/components/ui/collapsible";
import { Checkbox } from "@/components/ui/checkbox";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { DOC_TYPES, DOC_TYPE_OPTIONS, STATUS_TABS } from "@/lib/attachmentConstants";
import { formatKr, formatDate, formatMonth } from "@/lib/format";
import { toast } from "sonner";
import { Search, X, FolderOpen, FileText, Image as ImageIcon, FileCode2, Link2, ChevronLeft, ChevronRight, Archive, ChevronDown, ChevronUp, CheckCircle2, Tags, Trash2, Loader2, CheckSquare } from "lucide-react";
import { cn } from "@/lib/utils";

const fileIcon = (kind) => (kind === "pdf" ? FileText : kind === "ehf" ? FileCode2 : ImageIcon);

const LinkChip = ({ item }) => {
  const inv = item.linked?.invoice;
  const si = item.linked?.sales_invoice;
  if (inv) return <Link to={`/fakturaer/${inv.id}`} onClick={(e) => e.stopPropagation()} className="inline-flex items-center gap-1 text-xs text-primary hover:underline" data-testid="bilag-link-invoice-chip"><Link2 className="h-3 w-3" /> Regning</Link>;
  if (si) return <Link to={`/faktura/fakturaer/${si.id}`} onClick={(e) => e.stopPropagation()} className="inline-flex items-center gap-1 text-xs text-primary hover:underline" data-testid="bilag-link-sales-chip"><Link2 className="h-3 w-3" /> Faktura {si.number_display}</Link>;
  return <span className="text-xs text-muted-foreground">—</span>;
};

export default function AttachmentsPage() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { canWrite } = useAuth();
  const [data, setData] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [qInput, setQInput] = useState(params.get("q") || "");
  const [importOpen, setImportOpen] = useState(params.get("import") === "1");
  const [selected, setSelected] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState("");
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);

  const status = params.get("status") || "alle";
  const docType = params.get("type") || "";
  const year = params.get("year") || "";
  const linked = params.get("linked") || "";
  const q = params.get("q") || "";
  const page = Number(params.get("page") || 1);

  const setParam = useCallback((key, value) => {
    const next = new URLSearchParams(params);
    if (!value || value === "alle") next.delete(key); else next.set(key, value);
    if (key !== "page") next.delete("page");
    setParams(next, { replace: true });
  }, [params, setParams]);

  const load = useCallback(async () => {
    setError("");
    try {
      const [list, summ] = await Promise.all([
        api.get("/attachments", { params: { status: status === "alle" ? undefined : status, doc_type: docType || undefined, year: year || undefined, linked: linked || undefined, q: q || undefined, page, limit: 50 } }),
        api.get("/attachments/summary"),
      ]);
      setData(list.data);
      setSummary(summ.data);
    } catch (e) {
      setError(errorMessage(e, "Kunne ikke laste bilagsbanken"));
    } finally {
      setLoading(false);
    }
  }, [status, docType, year, linked, q, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setQInput(q); }, [q]);
  useEffect(() => { if (summary && summary.total === 0) setImportOpen(true); }, [summary]);

  const onImported = useCallback(() => { load(); }, [load]);

  const submitSearch = (e) => { e.preventDefault(); setParam("q", qInput.trim()); };
  const hasFilters = !!(docType || year || linked || q || status !== "alle");
  const reset = () => { setParams(new URLSearchParams(), { replace: true }); setQInput(""); };

  const typeChips = useMemo(() => [
    { value: "", label: `Alle typer${summary ? ` (${summary.total})` : ""}` },
    ...Object.entries(DOC_TYPES).map(([v, cfg]) => ({ value: v, label: `${cfg.label}${summary ? ` (${summary.by_type?.[v] || 0})` : ""}`, icon: cfg.icon })),
    { value: "ukjent", label: `Ikke klassifisert${summary ? ` (${summary.by_type?.ukjent || 0})` : ""}` },
  ], [summary]);

  const items = data?.items || [];
  const visibleIds = useMemo(() => items.map((a) => a.id), [items]);
  const selectedVisible = useMemo(() => visibleIds.filter((id) => selected.has(id)), [visibleIds, selected]);
  const allVisibleSelected = visibleIds.length > 0 && selectedVisible.length === visibleIds.length;
  const selectedItems = useMemo(() => items.filter((a) => selected.has(a.id)), [items, selected]);
  const selectedWithoutType = selectedItems.filter((a) => !a.doc_type).length;
  const selectedUnconfirmed = selectedItems.filter((a) => a.status !== "bekreftet").length;

  // Nullstill markering når filter/side endres (markeringen gjelder synlige bilag)
  useEffect(() => { setSelected(new Set()); }, [status, docType, year, linked, q, page]);

  const toggleOne = (id, on) => setSelected((prev) => { const n = new Set(prev); if (on) n.add(id); else n.delete(id); return n; });
  const toggleAllVisible = (on) => setSelected(() => (on ? new Set(visibleIds) : new Set()));
  const clearSelection = () => setSelected(new Set());

  const describeResult = (res, verb) => {
    const skipped = res.skipped || [];
    const reasons = skipped.reduce((acc, s) => { acc[s.reason] = (acc[s.reason] || 0) + 1; return acc; }, {});
    const skippedText = skipped.length ? ` · ${skipped.length} hoppet over (${Object.entries(reasons).map(([r, n]) => `${n} ${r}`).join(", ")})` : "";
    return `${res.processed} bilag ${verb}${skippedText}`;
  };

  const runBulk = async (action, extra = {}, verb = "behandlet") => {
    const ids = [...selected];
    if (!ids.length) return;
    setBulkBusy(action + (extra.doc_type || ""));
    try {
      const r = await api.post("/attachments/bulk", { action, ids, ...extra });
      const res = r.data;
      if (res.processed === 0 && res.skipped?.length) toast.warning(`Ingen bilag ${verb}`, { description: describeResult(res, verb) });
      else toast.success(describeResult(res, verb));
      // Behold markering på de som ble hoppet over ved bekreftelse (så de kan få type og bekreftes)
      if (action === "confirm") setSelected(new Set((res.skipped || []).map((s) => s.id).filter((id) => visibleIds.includes(id))));
      else if (action === "delete") clearSelection();
      await load();
    } catch (e) {
      toast.error(errorMessage(e, "Bulk-handlingen mislyktes"));
    } finally {
      setBulkBusy("");
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Arkiv"
        title="Bilagsbank"
        description="Samle bilder og PDF av regninger, fakturaer og kvitteringer. Vi leser dem for deg og foreslår type, motpart, beløp og periode – du bekrefter."
        actions={canWrite ? (
          <Button variant={importOpen ? "outline" : "default"} onClick={() => setImportOpen((o) => !o)} aria-expanded={importOpen} data-testid="bilag-toggle-import">
            <FolderOpen className="h-4 w-4 mr-2" /> {importOpen ? "Skjul import" : "Importer bilag"} {importOpen ? <ChevronUp className="h-4 w-4 ml-1" /> : <ChevronDown className="h-4 w-4 ml-1" />}
          </Button>
        ) : null}
      />

      <Collapsible open={importOpen}>
        <CollapsibleContent>
          <ImportPanel onImported={onImported} canWrite={canWrite} />
        </CollapsibleContent>
      </Collapsible>

      {summary && summary.total > 0 ? (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="bilag-summary">
          {[
            ["Bilag totalt", summary.total, null],
            ["Ikke lest", summary.by_status?.ny || 0, "ny"],
            ["Foreslått – trenger bekreftelse", summary.by_status?.foreslatt || 0, "foreslatt"],
            ["Uten kobling", summary.unlinked_invoices || 0, "unlinked"],
          ].map(([label, value, key]) => (
            <button
              key={label}
              type="button"
              onClick={() => { if (key === "unlinked") { const n = new URLSearchParams(); n.set("linked", "false"); setParams(n, { replace: true }); } else if (key) setParam("status", key); else reset(); }}
              className={cn("rounded-xl border bg-card p-4 text-left transition-colors hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring", ((key && key !== "unlinked" && status === key) || (key === "unlinked" && linked === "false")) && "border-primary")}
              data-testid={`bilag-kpi-${key || "total"}`}
            >
              <p className="text-xs text-muted-foreground">{label}</p>
              <p className="text-2xl font-semibold tnum mt-1">{value}</p>
            </button>
          ))}
        </div>
      ) : null}

      <div className="space-y-3">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <Tabs value={status} onValueChange={(v) => setParam("status", v)}>
            <TabsList className="h-auto flex-wrap justify-start" data-testid="bilag-status-tabs">
              {STATUS_TABS.map((t) => <TabsTrigger key={t.value} value={t.value} data-testid={`bilag-tab-${t.value}`}>{t.label}{summary && t.value !== "alle" ? <span className="ml-1.5 tnum text-muted-foreground">{summary.by_status?.[t.value] || 0}</span> : null}</TabsTrigger>)}
            </TabsList>
          </Tabs>
          <form onSubmit={submitSearch} className="relative w-full lg:w-80" role="search">
            <Search className="h-4 w-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
            <Input value={qInput} onChange={(e) => setQInput(e.target.value)} placeholder="Søk i motpart, filnavn, bilagsnr, beløp …" className="pl-9 pr-9" aria-label="Søk i bilagsbanken" data-testid="bilag-search-input" />
            {qInput ? <button type="button" onClick={() => { setQInput(""); setParam("q", ""); }} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" aria-label="Tøm søk" data-testid="bilag-search-clear"><X className="h-4 w-4" /></button> : null}
          </form>
        </div>

        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="flex flex-wrap gap-1.5" role="tablist" aria-label="Bilagstype" data-testid="bilag-type-filter">
            {typeChips.map((c) => (
              <button key={c.value || "alle"} type="button" role="tab" aria-selected={docType === c.value} onClick={() => setParam("type", c.value)} className={cn("inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm transition-colors", docType === c.value ? "bg-accent text-accent-foreground border-transparent" : "hover:bg-secondary")} data-testid={`bilag-type-${c.value || "alle"}`}>
                {c.icon ? <c.icon className="h-3.5 w-3.5" /> : null}{c.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <Select value={year || "alle"} onValueChange={(v) => setParam("year", v === "alle" ? "" : v)}>
              <SelectTrigger className="w-[130px]" aria-label="År" data-testid="bilag-year-select"><SelectValue placeholder="År" /></SelectTrigger>
              <SelectContent className="bg-card">
                <SelectItem value="alle">Alle år</SelectItem>
                {(summary?.years || []).map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={linked || "alle"} onValueChange={(v) => setParam("linked", v === "alle" ? "" : v)}>
              <SelectTrigger className="w-[160px]" aria-label="Kobling" data-testid="bilag-linked-select"><SelectValue placeholder="Kobling" /></SelectTrigger>
              <SelectContent className="bg-card">
                <SelectItem value="alle">Alle koblinger</SelectItem>
                <SelectItem value="true">Koblet</SelectItem>
                <SelectItem value="false">Uten kobling</SelectItem>
              </SelectContent>
            </Select>
            {hasFilters ? <Button variant="ghost" size="sm" onClick={reset} data-testid="bilag-filters-reset"><X className="h-4 w-4 mr-1" /> Nullstill</Button> : null}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="space-y-2" data-testid="bilag-loading"><Skeleton className="h-12" /><Skeleton className="h-12" /><Skeleton className="h-12" /></div>
      ) : error ? (
        <EmptyState title="Kunne ikke laste bilagsbanken" description={error} actionLabel="Prøv igjen" onAction={load} testId="bilag-error" />
      ) : items.length === 0 ? (
        summary?.total === 0 ? (
          <EmptyState icon={Archive} title="Bilagsbanken er tom" description="Velg en mappe på maskinen din eller dra inn bilder og PDF-er. Vi leser dem automatisk og foreslår type, motpart, beløp og periode." actionLabel={canWrite ? "Velg mappe eller filer" : undefined} onAction={() => { setImportOpen(true); window.scrollTo({ top: 0, behavior: "smooth" }); }} testId="bilag-empty-state" />
        ) : (
          <EmptyState icon={Search} title="Ingen treff" description="Prøv et annet søk eller fjern noen filtre." actionLabel="Nullstill filtre" onAction={reset} testId="bilag-no-results" />
        )
      ) : (
        <>
          {/* Desktop: tabell */}
          <div className="hidden md:block rounded-xl border bg-card overflow-hidden" data-testid="bilag-table">
            <Table>
              <TableHeader>
                <TableRow>
                  {canWrite ? <TableHead className="w-10"><Checkbox checked={allVisibleSelected ? true : selectedVisible.length ? "indeterminate" : false} onCheckedChange={(v) => toggleAllVisible(v === true)} aria-label="Velg alle synlige bilag" data-testid="bilag-select-all" /></TableHead> : null}
                  <TableHead>Bilag</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Motpart</TableHead>
                  <TableHead>Dato</TableHead>
                  <TableHead className="text-right">Beløp</TableHead>
                  <TableHead>Bilagsnr</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Kobling</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((a) => {
                  const Icon = fileIcon(a.kind);
                  return (
                    <TableRow key={a.id} onClick={() => navigate(`/bilag/${a.id}`)} className={cn("cursor-pointer h-14", selected.has(a.id) && "bg-accent/40 hover:bg-accent/50")} aria-selected={selected.has(a.id)} data-testid={`bilag-row-${a.id}`}>
                      {canWrite ? <TableCell className="w-10" onClick={(e) => e.stopPropagation()}><Checkbox checked={selected.has(a.id)} onCheckedChange={(v) => toggleOne(a.id, v === true)} aria-label={`Velg ${a.filename}`} data-testid={`bilag-select-${a.id}`} /></TableCell> : null}
                      <TableCell className="max-w-[280px]">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="h-9 w-9 rounded-lg bg-secondary flex items-center justify-center shrink-0"><Icon className="h-4 w-4 text-muted-foreground" /></div>
                          <div className="min-w-0">
                            <p className="font-medium truncate" title={a.filename}>{a.filename}</p>
                            <p className="text-xs text-muted-foreground truncate">{a.fields?.period ? formatMonth(a.fields.period) : formatDate(a.created_at?.slice(0, 10))}{a.source_path && a.source_path !== a.filename ? ` · ${a.source_path}` : ""}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell><DocTypeBadge docType={a.doc_type} short /></TableCell>
                      <TableCell className="max-w-[200px] truncate">{a.fields?.counterparty || <span className="text-muted-foreground">—</span>}</TableCell>
                      <TableCell className="tnum whitespace-nowrap">{a.fields?.date ? formatDate(a.fields.date) : <span className="text-muted-foreground">—</span>}</TableCell>
                      <TableCell className="tnum text-right whitespace-nowrap">{a.fields?.amount != null ? formatKr(a.fields.amount, a.fields.currency) : <span className="text-muted-foreground">—</span>}</TableCell>
                      <TableCell className="tnum">{a.fields?.voucher_no || <span className="text-muted-foreground">—</span>}</TableCell>
                      <TableCell><AttachmentStatusBadge status={a.status} /></TableCell>
                      <TableCell><LinkChip item={a} /></TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          {/* Mobil: kort */}
          {canWrite ? (
            <label className="md:hidden flex items-center gap-2 text-sm text-muted-foreground px-1">
              <Checkbox checked={allVisibleSelected ? true : selectedVisible.length ? "indeterminate" : false} onCheckedChange={(v) => toggleAllVisible(v === true)} aria-label="Velg alle synlige bilag" data-testid="bilag-select-all-mobile" /> Velg alle på siden
            </label>
          ) : null}
          <ul className="md:hidden space-y-2" data-testid="bilag-card-list">
            {items.map((a) => {
              const Icon = fileIcon(a.kind);
              return (
                <li key={a.id} className="flex items-start gap-2">
                  {canWrite ? <div className="pt-4 pl-1"><Checkbox checked={selected.has(a.id)} onCheckedChange={(v) => toggleOne(a.id, v === true)} aria-label={`Velg ${a.filename}`} data-testid={`bilag-select-mobile-${a.id}`} /></div> : null}
                  <button type="button" onClick={() => navigate(`/bilag/${a.id}`)} className={cn("flex-1 min-w-0 text-left rounded-xl border bg-card p-4 hover:bg-secondary transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring", selected.has(a.id) && "border-primary/50 bg-accent/30")} data-testid={`bilag-card-${a.id}`}>
                    <div className="flex items-start gap-3">
                      <div className="h-10 w-10 rounded-lg bg-secondary flex items-center justify-center shrink-0"><Icon className="h-5 w-5 text-muted-foreground" /></div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <p className="font-medium truncate">{a.fields?.counterparty || a.filename}</p>
                          <span className="tnum font-semibold whitespace-nowrap">{a.fields?.amount != null ? formatKr(a.fields.amount, a.fields.currency) : ""}</span>
                        </div>
                        <p className="text-xs text-muted-foreground truncate">{a.fields?.counterparty ? a.filename : ""}{a.fields?.date ? ` · ${formatDate(a.fields.date)}` : ""}{a.fields?.voucher_no ? ` · nr ${a.fields.voucher_no}` : ""}</p>
                        <div className="mt-2 flex flex-wrap items-center gap-1.5">
                          <DocTypeBadge docType={a.doc_type} short />
                          <AttachmentStatusBadge status={a.status} />
                          <LinkChip item={a} />
                        </div>
                      </div>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>

          {data.pages > 1 ? (
            <div className="flex items-center justify-between text-sm" data-testid="bilag-pagination">
              <p className="text-muted-foreground">Side {data.page} av {data.pages} · {data.total} bilag</p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setParam("page", String(page - 1))} data-testid="bilag-page-prev"><ChevronLeft className="h-4 w-4" /> Forrige</Button>
                <Button variant="outline" size="sm" disabled={page >= data.pages} onClick={() => setParam("page", String(page + 1))} data-testid="bilag-page-next">Neste <ChevronRight className="h-4 w-4" /></Button>
              </div>
            </div>
          ) : null}

          {/* Bulk-verktøylinje */}
          {canWrite && selected.size > 0 ? (
            <div className="sticky bottom-3 z-30 mx-auto w-full max-w-3xl rounded-xl border bg-card/95 backdrop-blur shadow-lg px-3 py-2.5 flex flex-wrap items-center gap-2" role="region" aria-label="Handlinger for valgte bilag" data-testid="bilag-bulk-toolbar">
              <span className="inline-flex items-center gap-1.5 text-sm font-medium pr-1" data-testid="bilag-bulk-count"><CheckSquare className="h-4 w-4 text-primary" /> {selected.size} valgt</span>
              <Button size="sm" onClick={() => runBulk("confirm", {}, "bekreftet")} disabled={!!bulkBusy || selectedUnconfirmed === 0} title={selectedWithoutType ? `${selectedWithoutType} mangler bilagstype og blir hoppet over` : undefined} data-testid="bilag-bulk-confirm">
                {bulkBusy === "confirm" ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <CheckCircle2 className="h-4 w-4 mr-1.5" />} Bekreft valgte{selectedUnconfirmed && selectedUnconfirmed !== selected.size ? ` (${selectedUnconfirmed})` : ""}
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="sm" variant="outline" disabled={!!bulkBusy} data-testid="bilag-bulk-type"><Tags className="h-4 w-4 mr-1.5" /> Sett type <ChevronDown className="h-3.5 w-3.5 ml-1" /></Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="bg-card w-64">
                  <DropdownMenuLabel className="text-xs text-muted-foreground">Sett type på {selected.size} bilag</DropdownMenuLabel>
                  {DOC_TYPE_OPTIONS.map((o) => <DropdownMenuItem key={o.value} onClick={() => runBulk("set_doc_type", { doc_type: o.value }, `satt til ${o.label.toLowerCase()}`)} data-testid={`bilag-bulk-type-${o.value}`}><o.icon className="h-4 w-4 mr-2" /> {o.label}</DropdownMenuItem>)}
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel className="text-xs text-muted-foreground">Sett type og bekreft</DropdownMenuLabel>
                  {DOC_TYPE_OPTIONS.map((o) => <DropdownMenuItem key={`c-${o.value}`} onClick={() => runBulk("confirm", { doc_type: o.value }, `bekreftet som ${o.label.toLowerCase()}`)} data-testid={`bilag-bulk-confirm-as-${o.value}`}><CheckCircle2 className="h-4 w-4 mr-2" /> Bekreft som {o.label.toLowerCase()}</DropdownMenuItem>)}
                </DropdownMenuContent>
              </DropdownMenu>
              <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive" onClick={() => setBulkDeleteOpen(true)} disabled={!!bulkBusy} data-testid="bilag-bulk-delete"><Trash2 className="h-4 w-4 mr-1.5" /> Slett</Button>
              {selectedWithoutType ? <span className="text-xs text-muted-foreground" data-testid="bilag-bulk-missing-type">{selectedWithoutType} mangler type – velg «Sett type» eller «Bekreft som …»</span> : null}
              <Button size="sm" variant="ghost" className="ml-auto" onClick={clearSelection} data-testid="bilag-bulk-clear"><X className="h-4 w-4 mr-1" /> Avbryt</Button>
            </div>
          ) : null}

          <AlertDialog open={bulkDeleteOpen} onOpenChange={setBulkDeleteOpen}>
            <AlertDialogContent className="bg-card" data-testid="bilag-bulk-delete-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle>Slette {selected.size} bilag?</AlertDialogTitle>
                <AlertDialogDescription>Bilagene fjernes fra bilagsbanken og filene slettes permanent. Regninger som er opprettet fra bilagene beholder sitt dokument. Dette kan ikke angres.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="bilag-bulk-delete-cancel">Avbryt</AlertDialogCancel>
                <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={() => { setBulkDeleteOpen(false); runBulk("delete", {}, "slettet"); }} data-testid="bilag-bulk-delete-confirm">Slett {selected.size} bilag</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </>
      )}
    </div>
  );
}
